import { MAP_ACTION } from "./constants.ts";
import capitalizeAll from "utils/capitalizeAll";

// TODO: we need to confirm how the normalizeString to be implemented
export function normalizeString(str) {
  return str.toLowerCase().replace(/[_-]/g, " ");
}

export function removeTrailingNumber(str) {
  if (str.includes("badge type")) {
    return str.replace(/\s*\d+$/, "").trim();
  }

  return str;
}

/**
 * Computes the list of DB field values that are actually mapped —
 * selectedHead has a real value AND mapActions for that column is MAP.
 * This prevents auto-mapped values that were later ignored from leaking
 * into validation checks.
 */
function getMappedValues(selectedHead, mapActions) {
  return Object.entries(selectedHead)
    .filter(
      ([index, value]) =>
        value && value !== "-" && mapActions[index] !== MAP_ACTION.NO_MAP,
    )
    .map(([, value]) => value);
}

/**
 * Returns true if any CSV column has not yet been decided — i.e. it is
 * set to "Map" but no database field has been selected for it.
 * Columns explicitly set to "Ignore" (NO_MAP) are treated as decided.
 *
 * @param {Record<number, string>} mapActions  – keyed by column index
 * @param {Record<number, string | null>} selectedHead – keyed by column index
 * @returns {boolean}
 */
export function hasUndecidedColumns(mapActions, selectedHead) {
  return Object.keys(mapActions).some((index) => {
    if (mapActions[index] === MAP_ACTION.NO_MAP) return false;

    const value = selectedHead[index];

    return value === "-" || !value;
  });
}

/**
 * Returns the display names of required head fields that have not been mapped
 * to any CSV column (or whose column is set to NO_MAP).
 *
 * @param {Array<{ name: string, required: boolean }>} headFields
 * @param {Record<number, string>} selectedHead – keyed by column index
 * @param {Record<number, string>} mapActions – keyed by column index
 * @returns {string[]} title-cased names of unmapped required fields
 */
export function getUnmappedRequiredFields(
  headFields,
  selectedHead,
  mapActions,
) {
  const mappedValues = getMappedValues(selectedHead, mapActions);

  return headFields
    .filter(
      (f) => f.required && !mappedValues.includes(normalizeString(f.name)),
    )
    .map((f) => capitalizeAll(normalizeString(f.name)));
}

/**
 * Returns a Set of normalized header names that are actively mapped —
 * i.e. mapActions for that column is MAP (not NO_MAP) and a real value was selected.
 *
 * @param {Record<number, string | null>} selectedHead
 * @param {Record<number, string>} mapActions
 * @returns {Set<string>}
 */
export function getMappedHeadersSet(selectedHead, mapActions) {
  return new Set(
    Object.entries(selectedHead)
      .filter(
        ([index, header]) =>
          mapActions[index] !== MAP_ACTION.NO_MAP &&
          typeof header === "string" &&
          header !== "-",
      )
      .map(([, header]) => normalizeString(header)),
  );
}

/**
 * Builds a map of group name → Set of normalized field names
 * for all fields with validation_rule "at_least_one_from_group".
 *
 * @param {Array<{ name: string, validation_rule?: string, group?: string }>} headFields
 * @returns {Record<string, Set<string>>}
 */
export function buildGroupedValidationRules(headFields) {
  return headFields.reduce((acc, field) => {
    if (
      field?.validation_rule === "at_least_one_from_group" &&
      field?.group &&
      field?.name
    ) {
      const groupName = field.group;

      if (!acc[groupName]) {
        acc[groupName] = new Set();
      }

      acc[groupName].add(normalizeString(field.name));
    }

    return acc;
  }, {});
}

/**
 * Returns the name of the first group that has no mapped field, or null if all groups pass.
 *
 * @param {Record<string, Set<string>>} groupedValidationRules
 * @param {Set<string>} mappedHeadersSet
 * @returns {string | null}
 */
export function getFailedGroupName(groupedValidationRules, mappedHeadersSet) {
  const failedEntry = Object.entries(groupedValidationRules).find(
    ([, groupFields]) =>
      Array.from(groupFields).every(
        (fieldName) => !mappedHeadersSet.has(fieldName),
      ),
  );

  return failedEntry ? failedEntry[0] : null;
}

/**
 * Returns error messages for groups with validation_rule "at_least_one_from_group"
 * where no field from that group is currently mapped.
 *
 * @param {Array<{ name: string, label?: string, validation_rule?: string, group?: string }>} headFields
 * @param {Record<number, string>} selectedHead – keyed by column index
 * @param {Record<number, string>} mapActions – keyed by column index
 * @returns {string[]} error messages for failed group rules
 */
export function getAtLeastOneFromGroupErrors(
  headFields,
  selectedHead,
  mapActions,
) {
  const groupedValidationRules = buildGroupedValidationRules(headFields);
  const mappedHeadersSet = getMappedHeadersSet(selectedHead, mapActions);

  return Object.entries(groupedValidationRules).reduce(
    (errors, [groupName, groupFields]) => {
      const hasAtLeastOneMapped = Array.from(groupFields).some((fieldName) =>
        mappedHeadersSet.has(fieldName),
      );

      if (hasAtLeastOneMapped) return errors;

      const groupFieldNames = headFields
        .filter(
          (field) =>
            field?.validation_rule === "at_least_one_from_group" &&
            field?.group === groupName,
        )
        .map(
          (field) =>
            field.label ||
            capitalizeAll(removeTrailingNumber(normalizeString(field.name))),
        );

      const displayGroupName = capitalizeAll(normalizeString(groupName));

      return [
        ...errors,
        `At least one field from the ${displayGroupName} group should be mapped: ${groupFieldNames.join(", ")}`,
      ];
    },
    [],
  );
}

/**
 * Returns true if the given normalized field name belongs to a group
 * that has no mapped field yet (i.e. its group validation is failing).
 *
 * @param {string | null | undefined} normalizedFieldName
 * @param {Array<{ name: string, validation_rule?: string, group?: string }>} headFields
 * @param {Record<string, Set<string>>} groupedValidationRules
 * @param {Set<string>} mappedHeadersSet
 * @returns {boolean}
 */
export function isGroupFieldMissing(
  normalizedFieldName,
  headFields,
  groupedValidationRules,
  mappedHeadersSet,
) {
  if (!normalizedFieldName) return false;

  const fieldMeta = headFields.find(
    (f) => normalizeString(f.name) === normalizedFieldName,
  );

  if (
    fieldMeta?.validation_rule !== "at_least_one_from_group" ||
    !fieldMeta?.group
  ) {
    return false;
  }

  const groupFields = groupedValidationRules[fieldMeta.group];

  return (
    groupFields !== undefined &&
    Array.from(groupFields).every((name) => !mappedHeadersSet.has(name))
  );
}

/**
 * Returns error messages for paired fields where only one of the pair is mapped.
 * A paired field relationship is defined by the `paired_with` property.
 * Both fields must either be mapped together or both be ignored — mapping only
 * one of them is a validation error.
 *
 * @param {Array<{ name: string, required: boolean, paired_with?: string, label?: string }>} headFields
 * @param {Record<number, string>} selectedHead – keyed by column index
 * @param {Record<number, string>} mapActions – keyed by column index
 * @returns {string[]} error messages for violated pairs
 */
export function getPairedFieldErrors(headFields, selectedHead, mapActions) {
  const mappedValues = getMappedValues(selectedHead, mapActions);

  const processedPairs = new Set();

  return headFields.reduce((errors, field) => {
    if (!field.paired_with) return errors;

    const pairKey = [field.name, field.paired_with].sort().join(":");

    if (processedPairs.has(pairKey)) return errors;

    processedPairs.add(pairKey);

    const fieldMapped = mappedValues.includes(normalizeString(field.name));
    const pairedField = headFields.find((f) => f.name === field.paired_with);

    const pairedMapped = pairedField
      ? mappedValues.includes(normalizeString(pairedField.name))
      : false;

    if (fieldMapped === pairedMapped) return errors;

    const nameA = capitalizeAll(normalizeString(field.name));

    const nameB = pairedField
      ? capitalizeAll(normalizeString(pairedField.name))
      : field.paired_with;

    return [...errors, `${nameA} and ${nameB} should be mapped`];
  }, []);
}
